import { DataSetConfig, MockDataConfig, MockDataSchema, AnalyticsDataPoint, MockFieldSchema } from '../types/analytics.types';
import { DateConstantsService } from './date-constants.service';

/**
 * Generic Mock Data Generator Service
 * 
 * Supports three strategies for generating mock data:
 * 1. Template-based: Use predefined templates (like existing sales template)
 * 2. Schema-based: Generate data from field schemas and business rules
 * 3. Custom: Use custom generation functions
 * 
 * This removes domain-specific logic from the generic DataSetManager
 * while supporting both predefined templates and AI agent-generated schemas.
 */
export class MockDataGenerator {
  
  /**
   * Generate mock data based on dataset configuration
   */
  async generateMockData(
    dataSet: DataSetConfig, 
    filters: Record<string, any> = {}
  ): Promise<AnalyticsDataPoint[]> {
    
    const mockConfig = dataSet.mockDataConfig;
    if (!mockConfig) {
      console.warn(`⚠️ [MockDataGenerator] No mock data config for dataset: ${dataSet.id}`);
      return [];
    }

            // Reduced debug logging for performance

    switch (mockConfig.strategy) {
      case 'template':
        return this.generateFromTemplate(dataSet, mockConfig, filters);
      
      case 'generated':
        return this.generateFromSchema(dataSet, mockConfig, filters);
      
      case 'custom':
        return this.generateFromCustomFunction(dataSet, mockConfig, filters);
      
      default:
        console.error(`❌ [MockDataGenerator] Unknown mock data strategy: ${mockConfig.strategy}`);
        throw new Error(`Unknown mock data strategy: ${mockConfig.strategy}`);
    }
  }

  /**
   * Generate data using predefined templates
   */
  private async generateFromTemplate(
    dataSet: DataSetConfig,
    mockConfig: MockDataConfig,
    filters: Record<string, any>
  ): Promise<AnalyticsDataPoint[]> {
    
    
    // 🔧 FIXED: Template strategy should just use the schema that's already in the config
    // No need for dynamic imports - everything we need is in mockConfig
    
    if (mockConfig.schema) {
      // If template has a schema, use it like the 'generated' strategy
      return this.generateFromSchema(dataSet, mockConfig, filters);
    }
    
    // 🚨 NO FALLBACK TO HARDCODED VALUES - Force proper schema configuration
    throw new Error(`Template strategy requires schema definition for dataset: ${dataSet.id}. No hardcoded fallback allowed.`);
  }

  /**
   * Generate data from schema definition
   */
  private async generateFromSchema(
    dataSet: DataSetConfig,
    mockConfig: MockDataConfig,
    filters: Record<string, any>
  ): Promise<AnalyticsDataPoint[]> {
    // Using schema-based generation
    
    const schema = mockConfig.schema;
    if (!schema) {
      throw new Error(`Schema strategy requires schema definition for dataset: ${dataSet.id}`);
    }


    const recordCount = this.getRecordCount(mockConfig);
    const temporalDistribution = this.getTemporalDistribution(mockConfig);
    const hierarchyConfig = mockConfig.hierarchyConfig;


    const data: AnalyticsDataPoint[] = [];

    // Generate hierarchical structure if configured
    const hierarchyData = hierarchyConfig ? 
      this.generateHierarchy(hierarchyConfig, schema) : 
      [{ enterprise: 'Default Enterprise', region: 'Default Region', location: 'Default Location' }];
    
    // Generate records for each temporal period
    for (const dateConstant of temporalDistribution.dateConstants) {
      // Check if the string is a valid date constant, otherwise use it as-is
      const resolvedDateString = DateConstantsService.isDateConstant(dateConstant) 
        ? DateConstantsService.resolveDateConstant(dateConstant)
        : dateConstant;
      const resolvedDate = new Date(resolvedDateString);
      
      // ✅ Validate that we have a valid date
      if (isNaN(resolvedDate.getTime())) {
        console.error(`❌ [MockDataGenerator] Invalid date generated from: "${dateConstant}" → "${resolvedDateString}"`);
        console.error(`❌ [MockDataGenerator] Skipping this temporal period to prevent dataset failure`);
        continue; // Skip this invalid date and continue with the next one
      }
      
      for (let h = 0; h < hierarchyData.length; h++) {
        const hierarchyRecord = hierarchyData[h];
        
        for (let r = 0; r < temporalDistribution.recordsPerConstant; r++) {
          const record = await this.generateRecord(schema, {
            dateConstant,
            resolvedDate,
            hierarchy: hierarchyRecord,
            recordIndex: r,
            filters
          });
          
          data.push(record);
        }
      }
    }

    return data;
  }

  /**
   * Generate data using custom function
   */
  private async generateFromCustomFunction(
    dataSet: DataSetConfig,
    mockConfig: MockDataConfig,
    filters: Record<string, any>
  ): Promise<AnalyticsDataPoint[]> {
    
    if (!mockConfig.customGenerator) {
      throw new Error(`Custom strategy requires customGenerator function for dataset: ${dataSet.id}`);
    }

    
    try {
      const data = await mockConfig.customGenerator(dataSet, filters);
      return data;
    } catch (error) {
      console.error(`❌ [MockDataGenerator] Custom generation failed:`, error);
      return [];
    }
  }

  /**
   * Generate a single record from schema
   */
  private async generateRecord(
    schema: MockDataSchema,
    context: {
      dateConstant: string;
      resolvedDate: Date;
      hierarchy: Record<string, any>;
      recordIndex: number;
      filters: Record<string, any>;
    }
  ): Promise<AnalyticsDataPoint> {
    
    const record: AnalyticsDataPoint = {
      id: `${schema.requiredFields.find(f => f.name === 'id')?.pattern || 'record'}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      date: context.resolvedDate.toISOString(),
      ...context.hierarchy
    };

    // Generate required fields
    for (const field of schema.requiredFields) {
      if (!record[field.name]) { // Don't override hierarchy fields
        record[field.name] = this.generateFieldValue(field, record, context);
      }
    }

    // Generate optional fields
    if (schema.optionalFields) {
      for (const field of schema.optionalFields) {
        record[field.name] = this.generateFieldValue(field, record, context);
      }
    }

    // Apply relationships and formulas
    if (schema.relationships) {
      for (const relationship of schema.relationships) {
        this.applyRelationship(record, relationship);
      }
    }

    // Post-process computed fields
    this.applyComputedFields(record);

    return record;
  }

  /**
   * Generate value for a specific field
   */
  private generateFieldValue(
    field: MockFieldSchema,
    record: AnalyticsDataPoint,
    context: any
  ): any {
    
    switch (field.type) {
      case 'string':
        if (field.values) {
          return field.values[Math.floor(Math.random() * field.values.length)];
        }
        return field.pattern || `${field.name}_${Math.random().toString(36).substr(2, 5)}`;
      
      case 'number':
      case 'currency':
        const range = field.range || { min: 1, max: 1000 };
        let baseValue = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
        
        // Apply variance modifiers if configured
        const modifiedValue = this.applyVarianceModifiers(baseValue, field, record, context);
        
        return field.type === 'currency' ? Math.round(modifiedValue * 100) : modifiedValue; // Convert to cents for currency
      
      case 'percentage':
        const pctRange = field.range || { min: 0, max: 100 };
        return Math.floor(Math.random() * (pctRange.max - pctRange.min + 1)) + pctRange.min;
      
      case 'date':
        return context.resolvedDate.toISOString();
      
      case 'boolean':
        return Math.random() > 0.5;
      
      case 'enum':
        if (field.enumValues) {
          const selectedValue = field.enumValues[Math.floor(Math.random() * field.enumValues.length)];
                          // Removed excessive debug logging for performance
          return selectedValue;
        }
        console.warn(`⚠️ [MockDataGenerator] Enum field ${field.name} has no enumValues defined`);
        return null;
      
      default:
        return null;
    }
  }

  /**
   * Apply business rule relationships
   */
  private applyRelationship(record: AnalyticsDataPoint, relationship: any): void {
    // Simple formula evaluation (could be enhanced with a proper expression parser)
    try {
      if (relationship.formula.includes('=')) {
        const [target, expression] = relationship.formula.split('=').map(s => s.trim());
        
        // Simple math operations
        let result = expression;
        relationship.fields.forEach((field: string) => {
          const value = record[field] || 0;
          result = result.replace(new RegExp(`\\b${field}\\b`, 'g'), value.toString());
        });
        
        // Evaluate simple math expressions (basic security: only allow numbers and basic operators)
        if (/^[0-9\+\-\*\/\.\(\)\s]+$/.test(result)) {
          record[target] = eval(result);
        }
      }
    } catch (error) {
      console.warn(`⚠️ [MockDataGenerator] Failed to apply relationship: ${relationship.formula}`, error);
    }
  }

  /**
   * Generate hierarchical data structure with proper parent-child relationships
   */
  private generateHierarchy(config: any, schema?: MockDataSchema): Record<string, any>[] {
    const { levels, branching, includeGeographic, relationships } = config;
    const hierarchies: Record<string, any>[] = [];
    
    // Ensure levels are defined
    if (!levels || !Array.isArray(levels) || levels.length === 0) {
      throw new Error('hierarchyConfig.levels must be defined as an array of field names - no hardcoded fallbacks allowed');
    }
    
    // Extract enumValues from schema if provided
    const getEnumValues = (fieldName: string): string[] => {
      if (!schema || !schema.requiredFields) {
        return [];
      }
      const field = schema.requiredFields.find(f => f.name === fieldName);
      // Handle both enumValues and values properties for flexibility
      const values = field?.enumValues || (field as any)?.values || [];
      return values;
    };
    
    // Get valid children for a parent at a specific level
    const getValidChildren = (parentLevel: string, parentValue: string, childLevel: string): string[] => {
      if (relationships && relationships[parentLevel] && relationships[parentLevel][parentValue] && relationships[parentLevel][parentValue][childLevel]) {
        return relationships[parentLevel][parentValue][childLevel];
      }
      // Fallback to all enum values if no relationship defined
      return getEnumValues(childLevel);
    };
    
    // Generate hierarchies recursively with relationship constraints
    const generateCombinations = (levelIndex: number, currentHierarchy: Record<string, any>): void => {
      if (levelIndex >= levels.length) {
        // We've reached the bottom level, add the complete hierarchy
        if (includeGeographic && levelIndex === levels.length) {
          // Add geographic data at the leaf level
          const countryCodes = ['US', 'CA', 'GB', 'AU'];
          currentHierarchy.country_code = countryCodes[Math.floor(Math.random() * countryCodes.length)];
        }
        hierarchies.push({ ...currentHierarchy });
        return;
      }
      
      const levelName = levels[levelIndex];
      let enumValues: string[];
      
      if (levelIndex === 0) {
        // Top level - use all enum values
        enumValues = getEnumValues(levelName);
      } else {
        // Child level - check for relationship constraints
        const parentLevel = levels[levelIndex - 1];
        const parentValue = currentHierarchy[parentLevel];
        enumValues = getValidChildren(parentLevel, parentValue, levelName);
      }
      
      if (enumValues.length === 0) {
        throw new Error(`No valid values found for hierarchy level '${levelName}' - check schema enumValues and relationships`);
      }
      
      const maxBranching = branching[levelIndex] || enumValues.length;
      
      // Use only up to maxBranching values for this level
      enumValues.slice(0, maxBranching).forEach(value => {
        const newHierarchy = {
          ...currentHierarchy,
          [levelName]: value,
          [`${levelName}_id`]: value.toLowerCase().replace(/\s+/g, '-')
        };
        
        // Recursively generate for the next level
        generateCombinations(levelIndex + 1, newHierarchy);
      });
    };
    
    // Start generating from level 0
    generateCombinations(0, {});
    
    return hierarchies;
  }

  /**
   * Utility methods
   */
  private getRecordCount(mockConfig: MockDataConfig): number {
    return mockConfig.recordCount?.default || 50;
  }

  private getTemporalDistribution(mockConfig: MockDataConfig): { dateConstants: string[]; recordsPerConstant: number } {
    return mockConfig.temporalDistribution || {
      dateConstants: ['TODAY', 'YESTERDAY', 'LAST_WEEK', 'LAST_MONTH'],
      recordsPerConstant: 2
    };
  }

  // 🚨 REMOVED: generateBasicMockData() 
  // This function contained hardcoded values like 'CAKE', specific regions, order types, etc.
  // All mock data generation must now use schema-based configuration.
  // Any missing schema configuration will throw clear error messages.

  private toPascalCase(str: string): string {
    return str.split('-').map(part => 
      part.charAt(0).toUpperCase() + part.slice(1)
    ).join('');
  }



  /**
   * Apply variance modifiers based on field configuration
   */
  private applyVarianceModifiers(
    baseValue: number, 
    field: MockFieldSchema, 
    record: AnalyticsDataPoint, 
    context: any
  ): number {
    // Check if field has variance modifiers configured
    const varianceModifiers = (field as any).varianceModifiers;
    if (!varianceModifiers) {
      return baseValue;
    }


    let modifiedValue = baseValue;

    // Apply modifiers based on enum field values in the record
    for (const modifier of varianceModifiers) {
      const { basedOnField, multipliers } = modifier;
      let fieldValue = record[basedOnField];
      
      // Handle derived fields like dayOfWeek
      if (basedOnField === 'dayOfWeek' && !fieldValue && context.resolvedDate) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        fieldValue = dayNames[context.resolvedDate.getDay()];
        record.dayOfWeek = fieldValue; // Store it for potential future use
      }
      
      if (fieldValue && multipliers[fieldValue]) {
        const multiplier = multipliers[fieldValue];
        modifiedValue = Math.round(modifiedValue * multiplier);
      }
    }

    return modifiedValue;
  }

  /**
   * Apply computed fields based on other field values
   */
  private applyComputedFields(record: AnalyticsDataPoint): void {
    // Customer Support fields
    if (record.status && (record.is_resolved !== undefined)) {
      record.is_resolved = (record.status === 'resolved' || record.status === 'closed') ? 1 : 0;
    }

    if (record.ticket_count !== undefined) {
      record.ticket_count = 1;
    }

    // Sales Dashboard fields
    if (record.item_sales !== undefined && record.discounts_comps !== undefined && record.refunds !== undefined) {
      record.net_sales = Number(record.item_sales) + Number(record.discounts_comps) + Number(record.refunds);
    }

    if (record.net_sales !== undefined && record.tax !== undefined) {
      record.gross_sales = Number(record.net_sales) + Number(record.tax);
    }

    if (record.gross_sales !== undefined && record.tips !== undefined && record.fees !== undefined) {
      record.total_sales = Number(record.gross_sales) + Number(record.tips) + Number(record.fees);
    }

    if (record.total_sales !== undefined && record.order_count !== undefined && Number(record.order_count) > 0) {
      record.avg_ticket = Number(record.total_sales) / Number(record.order_count);
    }

    if (record.guest_count !== undefined && record.order_count !== undefined && Number(record.order_count) > 0) {
      record.guests_per_order = Number(record.guest_count) / Number(record.order_count);
    }
  }
} 